import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';
import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';

/**
 * Clustered Column Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-clustered-column-graph',
  standalone: false,
  templateUrl: './clustered-column-graph.component.html',
  styleUrl: './clustered-column-graph.component.scss'
})
export class ClusteredColumnGraphComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  private clusteredColumnGraphReportSubscription: any;
  private isClusteredColumnViewInitialized = false;
  private isCreatingChart = false;

  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    this.zone.runOutsideAngular(() => {
      this.clusteredColumnGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Clustered Column Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          console.log("Clustered Column Transformed Data", this.data);
          if (this.isClusteredColumnViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isClusteredColumnViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('clustered-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          console.log("Clustered Column Transformed Data in ngAfterViewInit", this.data);
          this.updateChart(this.data);
        } else {
          console.log('Clustered Column Chart - DOM element not ready in ngAfterViewInit, will retry later');
        }
      });
    }
  }

  ngOnChanges(): void {
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0 && this.isClusteredColumnViewInitialized) {
      this.zone.runOutsideAngular(() => {
        // Check if DOM element exists before creating chart
        const chartContainer = document.getElementById('clustered-column-chartdiv');
        if (chartContainer) {
          this.data = this._baseChartService.transformReportData(this.reportData);
          console.log("Clustered Column Transformed Data in ngOnChanges", this.data);
          this.updateChart(this.data);
        } else {
          console.log('Clustered Column Chart - DOM element not ready in ngOnChanges, will retry later');
          // Retry after a short delay
          setTimeout(() => {
            const retryContainer = document.getElementById('clustered-column-chartdiv');
            if (retryContainer) {
              this.data = this._baseChartService.transformReportData(this.reportData);
              console.log("Clustered Column Transformed Data in retry", this.data);
              this.updateChart(this.data);
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Create chart with legacy PHP implementation features
   * @param data - Chart data
   * https://www.amcharts.com/demos/clustered-column-chart/
   */
  
  createChart(data: BarChartData[]) {
    // Prevent multiple chart creations
    if (this.isCreatingChart) {
      console.log('Clustered Column Chart - Chart creation already in progress, skipping...');
      return;
    }

    this.isCreatingChart = true;

    // Check if the chart container exists
    const chartContainer = document.getElementById('clustered-column-chartdiv');
    if (!chartContainer) {
      console.error('Clustered Column Chart - Chart container not found! Make sure element with id "clustered-column-chartdiv" exists');
      this.isCreatingChart = false;
      return;
    }

    // Dispose existing chart before creating new one
    if (this.root) {
      console.log('Clustered Column Chart - Disposing existing chart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }

    try {
      console.log('Clustered Column Chart - Creating new chart...');
      this.root = this._baseChartService.createRoot('clustered-column-chartdiv');
      this._baseChartService.attachExporting(this.root, { 
        filePrefix: 'clustered-column',
        data: data
      });

      // Dynamically determine category and value fields from data
      const categoryField = this._baseChartService.getCategoryField(data);
      const valueFields = this._baseChartService.getValueFields(data);

      // Create chart container following official pattern
      const chart = this.root.container.children.push(am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        paddingLeft: 0,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.root.verticalLayout
      }));

      // Create X-axis using base chart service for proper label configuration
      let xAxis = this._baseChartService.createCategoryAxis(chart, categoryField, {
        axisType: 'x',
        minGridDistance: 50,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9,
        rotation: 45
      });

      // Enhanced axis animation
      this._baseChartService.setupAxisAnimation(xAxis);

      xAxis.data.setAll(data);

      // Create Y-axis renderer following official pattern
      let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          strokeOpacity: 0.1
        })
      }));

      // Set minimum value
      yAxis.set('min', 0);

      // Create series for each value field following official pattern
      // valueFields.forEach((valueField, index) => {
      //   let series = chart.series.push(am5xy.ColumnSeries.new(this.root, {
      //     name: valueField,
      //     xAxis: xAxis,
      //     yAxis: yAxis,
      //     valueYField: valueField,
      //     categoryXField: categoryField
      //   }));

      //   // Configure columns following official pattern
      //   series.columns.template.setAll({
      //     width: am5.percent(90),
      //     tooltipY: 0,
      //     strokeOpacity: 0
      //   });

      //   // Set data first
      //   series.data.setAll(data);

      //   // Make stuff animate on load
      //   series.appear();

      //   // Configure tooltip using global function
      //   const tooltip = this._baseChartService.createStandardTooltip(this.root, 'column', {
      //     showSeries: true,
      //     showCategory: true,
      //     showValue: true
      //   });
        
      //   series.set('tooltip', tooltip);

      //   // Add to legend
      // });

      
      valueFields.forEach((valueField, index) => {
        let series = chart.series.push(
          am5xy.ColumnSeries.new(this.root, {
            name: valueField,
            xAxis: xAxis,
            yAxis: yAxis,
            valueYField: valueField,
            categoryXField: categoryField
          })
        );
       
        series.columns.template.setAll({
          width: am5.percent(90),
          tooltipY: 0,
          strokeOpacity: 0
        });
       
        // 🔥 FIX: tooltip only for this series
        series.columns.template.set(
          "tooltipText",
          "{name}: {valueY}"
        );
       
        series.data.setAll(data);
        series.appear();
      });

      // Add cursor
      chart.set('cursor', am5xy.XYCursor.new(this.root, {
        xAxis: xAxis
      }));

      // Add legend
      let legend = chart.children.push(
        this._baseChartService.createLegend(this.root)
      );

      this._baseChartService.setupLegendAnimation(legend);

      legend.set('maxHeight', 40);

      let markerTemplate = legend.markers.template;
      markerTemplate.set('width', 12);
      markerTemplate.set('height', 12);

      legend.data.setAll(chart.series.values);

      // Add value labels after chart is fully rendered
      setTimeout(() => {
        chart.series.each((series) => {
          series.bullets.push((root, series, dataItem) => {
            const bullet = am5.Bullet.new(root, {
              locationY: 0,
              sprite: am5.Label.new(root, {
                text: "{valueY.formatNumber('#,##0')}",
                fill: am5.color(0x000000),
                centerY: 0,
                centerX: am5.p50,
                populateText: true,
                fontSize: 11,
                dy: -25
              })
            });
            
            return bullet;
          });
        });
      }, 500);

      // Make stuff animate on load with timeout to ensure proper rendering
      setTimeout(() => {
        chart.appear(1000, 100);
      }, 100);

      console.log('Clustered Column Chart - Chart created successfully');
      this.isCreatingChart = false;

    } catch (error) {
      console.error('Error creating clustered column chart:', error);
      this.isCreatingChart = false;
    }
  }

  private updateChart(data: BarChartData[]) {
    // Prevent multiple chart updates
    if (this.isCreatingChart) {
      console.log('Clustered Column Chart - Chart update skipped, chart creation in progress...');
      return;
    }

    // Check if DOM element exists before proceeding
    const chartContainer = document.getElementById('clustered-column-chartdiv');
    if (!chartContainer) {
      console.log('Clustered Column Chart - DOM element not ready in updateChart, will retry later');
      // Retry after a short delay
      setTimeout(() => {
        this.updateChart(data);
      }, 100);
      return;
    }

    // Dispose existing chart first
    if (this.root) {
      console.log('Clustered Column Chart - Disposing chart in updateChart...');
      this._baseChartService.disposeChart(this.root);
      this.root = undefined as any;
    }
    
    // Add a longer delay to ensure proper disposal and DOM element availability
    setTimeout(() => {
      this.createChart(data);
    }, 200);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.clusteredColumnGraphReportSubscription) {
      this.clusteredColumnGraphReportSubscription.unsubscribe();
    }
  }
}
